// eslint-disable-next-line no-unused-vars
import React, { useEffect, useCallback, useState } from 'react';
import { number, bool, string, func } from 'prop-types';
import debounce from 'lodash/debounce';

import QuantitySelectComponent from 'Components/QuantitySelector/QuantitySelect';

const QuantitySelect = (
  { value = 1, step = 1, zeroAllowed = false, min = 1, max = 99, onChange, size = 'sm', ...rest }
) => {
  const [state, setState] = useState(value);

  useEffect(() => {
    if (value !== state) {
      setState(value);
    }
  }, [value, max]);

  useEffect(() => {
    if (
      // eslint-disable-next-line no-restricted-globals
      !isNaN(rest.product?.quantity)
      && rest.product.quantity !== state
    ) {
      setState(rest.product.quantity);
    }
  }, [rest.product]);

  const handleQuantityChange = useCallback(
    debounce(newQty => {
      if (
        newQty === 0
        && zeroAllowed
      ) {
        setState(0);
        onChange(0);
        return;
      }
      if (newQty > max) {
        const newValue = max - (max % step);
        setState(newValue);
        onChange(newValue);
      } else if (newQty < min) {
        setState(min);
        onChange(min);
      } else {
        const diffDown = (newQty % (min || 1));
        const diffUp = (Math.round(newQty / min) * min) - newQty;
        let newValue;
        if (diffUp < diffDown) {
          newValue = newQty + diffUp;
        } else {
          newValue = newQty - diffDown;
        }
        setState(newValue);
        onChange(newValue);
      }
    }, 250, { leading: false, trailing: true }),
    [max, min, step]
  );

  const debouncedInputChange = useCallback(
    debounce(rawValue => {
      const parsedValue = parseInt(rawValue, 10);
      if (
        // eslint-disable-next-line no-restricted-globals
        isNaN(parsedValue)
      ) {
        setState(value);
        return;
      }
      handleQuantityChange(parsedValue);
    }, 250, { leading: false, trailing: true }),
    [max, min]
  );

  const handleClickChange = rawValue => {
    const parsedValue = parseInt(rawValue, 10);
    setState(parsedValue);
    debouncedInputChange(rawValue);
  };

  const handleInputChange = evt => {
    handleClickChange(evt?.target?.value);
  };

  useEffect(() => () => {
    handleQuantityChange.cancel();
  }, [max, min, step]);

  useEffect(() => () => {
    debouncedInputChange.cancel();
  }, [max, min]);

  return (
    <QuantitySelectComponent
      onClick={ handleClickChange }
      onInputChange={ handleInputChange }
      zeroAllowed={ zeroAllowed }
      value={ state }
      step={ step }
      min={ min }
      max={ max }
      size={ size }
      { ...rest }
    />
  );
};

QuantitySelect.propTypes = {
  onChange: func.isRequired,
  zeroAllowed: bool,
  size: string,
  value: number,
  step: number,
  min: number,
  max: number
};

export default QuantitySelect;
